import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import useMobileDetect from "common/components/use-mobile-detect";
import CreativeTemplatePropType from "common/prop-types/creative-template-prop-type";
import AdDesignPreview from "partner-dashboard/components/ad-designs/ad-design-preview";
import PlacementSelectors from "creative-ad-builder/preview/placement-selectors";
import WebPreviewCarousel from "creative-ad-builder/preview/web-preview-carousel";
import BannerPreviewCarousel from "creative-ad-builder/preview/banner-preview-carousel";
import MobilePreviewCarousel from "creative-ad-builder/preview/mobile-preview-carousel";
import VideoPreviewPlacement from "creative-ad-builder/preview/video-preview-placement";
import CtvPreviewPlacement from "creative-ad-builder/preview/ctv-preview-placement";
import CreatomateAdPreview from "creative-ad-builder/preview/creatomate-ad-preview";
import FacebookPhonePlaceholder from "creative-ad-builder/preview/facebook-phone-placeholder";
import InstagramPhonePlaceholder from "creative-ad-builder/preview/instagram-phone-placeholder";
import "creative-ad-builder/preview/ad-preview-modal.styles.scss";

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  templates: PropTypes.arrayOf(CreativeTemplatePropType).isRequired,
  initialPlacement: PropTypes.string,
  productCode: PropTypes.string.isRequired,
  facebookPage: PropTypes.shape({
    name: PropTypes.string,
    picture: PropTypes.string
  })
};

const DEFAULT_FACEBOOK_PAGE = {};
const DEFAULT_FACEBOOK_NEWSFEED_DATA = {
  body: "Body",
  message: "Message",
  title: "Title",
  domain: "Domain"
};
const AVAILABLE_PLACEMENTS = [
  "preview_web",
  "preview_video",
  "preview_banner",
  "preview_mobile",
  "preview_facebook",
  "preview_instagram",
  "preview_dooh_1080x1920",
  "preview_dooh_1280x960",
  "preview_dooh_1400x400",
  "preview_dooh_1920x1080"
];

const CampaignAdPreviewModal = ({
  isOpen,
  onClose,
  templates,
  initialPlacement = null,
  productCode,
  facebookPage = DEFAULT_FACEBOOK_PAGE
}) => {
  const { isMobile } = useMobileDetect();
  const [selectedPlacement, setSelectedPlacement] = useState(initialPlacement);

  const placements = AVAILABLE_PLACEMENTS.filter(p =>
    templates.some(t => t.placements.includes(p))
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedPlacement(initialPlacement || placements[0] || null);
    }
  }, [isOpen, initialPlacement]);

  const templateForPlacement = placement => templates.find(t => t.placements.includes(placement));

  const renderAdDesignPreview = template => {
    if (template.isCreatomateTemplate) {
      return <CreatomateAdPreview key={template.displayName} creative={template} />;
    }
    return (
      <AdDesignPreview
        key={template.displayName}
        url={template.previewPath}
        mimeType={template.primaryMimeType}
        title={template.displayName}
        width={template.width}
        height={template.height}
        exactSize
      />
    );
  };

  const phonePlaceholderProps = template => ({
    creative: template,
    facebookPage: { name: facebookPage?.name, picture: facebookPage?.picture },
    facebookNewsfeedData: DEFAULT_FACEBOOK_NEWSFEED_DATA,
    isMobile,
    uniqueKey: template.displayName
  });

  const renderForPlacement = placement => {
    const template = templateForPlacement(placement);
    if (!template) return null;
    if (isMobile) {
      return renderAdDesignPreview(template);
    }
    if (placement === "preview_facebook") {
      return <FacebookPhonePlaceholder {...phonePlaceholderProps(template)} />;
    }
    if (placement === "preview_instagram") {
      return <InstagramPhonePlaceholder {...phonePlaceholderProps(template)} />;
    }
    if (placement === "preview_video") {
      return productCode === "ctv" ? (
        <CtvPreviewPlacement uniqueKey={template.displayName} template={template} />
      ) : (
        <VideoPreviewPlacement uniqueKey={template.displayName} template={template} />
      );
    }

    const carouselByPlacement = {
      preview_web: WebPreviewCarousel,
      preview_banner: BannerPreviewCarousel,
      preview_mobile: MobilePreviewCarousel
    };
    const Carousel = carouselByPlacement[placement];
    if (Carousel) return <Carousel template={template} />;

    return renderAdDesignPreview(template);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      className="ad-preview-modal"
      data-testid="campaign-ad-preview-modal"
      centered
    >
      <ModalHeader toggle={onClose} className="pb-0" />
      <ModalBody className="pt-0">
        <PlacementSelectors
          placements={placements}
          selectedPlacement={selectedPlacement}
          onSelectPlacement={setSelectedPlacement}
          productCode={productCode}
        />
        <div className="d-flex justify-content-around flex-wrap align-items-center ad-preview-modal__content">
          {AVAILABLE_PLACEMENTS.map(placement => (
            <div
              key={placement}
              style={{ display: selectedPlacement !== placement ? "none" : "block" }}
            >
              {renderForPlacement(placement)}
            </div>
          ))}
        </div>
      </ModalBody>
    </Modal>
  );
};

CampaignAdPreviewModal.propTypes = propTypes;

export default CampaignAdPreviewModal;
